// WAV encoding + real-time tap recorder for Web Audio nodes.

export function encodeWAV(channelBuffers, sampleRate) {
  const numChannels = channelBuffers.length;
  const numFrames = channelBuffers[0].length;
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const dataSize = numFrames * blockAlign;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeString = (offset, str) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 8 * bytesPerSample, true);
  writeString(36, "data");
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < numFrames; i++) {
    for (let c = 0; c < numChannels; c++) {
      let sample = Math.max(-1, Math.min(1, channelBuffers[c][i]));
      sample = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
      view.setInt16(offset, sample, true);
      offset += 2;
    }
  }
  return new Blob([view], { type: "audio/wav" });
}

function mergeChunks(chunks, length) {
  const out = new Float32Array(length);
  let pos = 0;
  for (const chunk of chunks) {
    out.set(chunk, pos);
    pos += chunk.length;
  }
  return out;
}

// Taps a node's output and captures raw PCM while recording.
export class TapRecorder {
  constructor(ctx, sourceNode) {
    this.ctx = ctx;
    this.source = sourceNode;
    this.processor = ctx.createScriptProcessor(4096, 2, 2);
    this.left = [];
    this.right = [];
    this.length = 0;
    this.recording = false;
    this.processor.onaudioprocess = (e) => {
      if (!this.recording) return;
      this.left.push(new Float32Array(e.inputBuffer.getChannelData(0)));
      this.right.push(new Float32Array(e.inputBuffer.getChannelData(1)));
      this.length += e.inputBuffer.length;
    };
    this.silent = ctx.createGain();
    this.silent.gain.value = 0;
    this.source.connect(this.processor);
    this.processor.connect(this.silent);
    this.silent.connect(ctx.destination);
  }
  start() {
    this.left = [];
    this.right = [];
    this.length = 0;
    this.recording = true;
  }
  stop() {
    this.recording = false;
    const l = mergeChunks(this.left, this.length);
    const r = mergeChunks(this.right, this.length);
    return encodeWAV([l, r], this.ctx.sampleRate);
  }
  dispose() {
    try {
      this.source.disconnect(this.processor);
    } catch (e) {}
    this.processor.disconnect();
    this.silent.disconnect();
  }
}

export function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
